import NoSearchResults from "./no-search-results.jsx";
import EmptyState from "./empty-state.jsx";
import { apiFetch, showAppState } from "./app-state.jsx";
import React from "react";
import "./careers.css";
import { SiteHeader as Header, SiteFooter as Footer } from "./chrome.jsx";
import { FaArrowRight, FaCloudArrowUp } from "react-icons/fa6";

function getApiUrl() {
  return process.env.REACT_APP_API_URL?.replace(/\/+$/, "");
}

function normalizeJob(job, index) {
  const description =
    job.description ||
    job.jobDescription ||
    job.jd ||
    job.details ||
    job.body ||
    job.requirements ||
    "";

  return {
    id: job.id || `${job.title}-${index}`,
    title: job.title || "Open Role",
    team: job.team || "General",
    location: job.location || "Bengaluru",
    description,
    status: job.status || "Open",
  };
}

function isOpenJob(job) {
  const status = String(job.status || "Open").trim().toLowerCase();
  return status !== "closed" && status !== "paused";
}

const fallbackJobDescriptions = {
  "sales & marketing": [
    "**Sales & Marketing Executive**",
    "**Position:** Sales & Marketing Executive",
    "**Experience:** 0-2 Years",
    "**Location:** Bengaluru / Hybrid",
    "**Employment:** Full-Time",
    "### Responsibilities:",
    "- Generate new leads through online and offline marketing activities.",
    "- Contact potential customers through calls, WhatsApp, email, and social media.",
    "- Understand customer requirements and explain products/services.",
    "- Follow up with leads and convert them into customers.",
    "- Maintain customer and lead records in CRM or Excel.",
    "- Build and maintain good relationships with existing customers.",
  ],
};

function getJobDescription(job) {
  const description = String(job.description || "").trim();
  if (description) return description;

  return fallbackJobDescriptions[String(job.title || "").trim().toLowerCase()]?.join("\n") || "";
}

function Hero() {
  return (
    <section className="career-hero">
      <div className="hero-copy">
        <p className="kicker">Careers at Hallef <span /></p>
        <h1>Grow Your<br />Career <span>With Us.</span></h1><br/>
        <a href="#positions">View Open Positions <FaArrowRight aria-hidden="true" /></a>
      </div>
      <div className="hero-art">
        {/* <div className="soft-block" aria-hidden="true" /> */}
        <figure>
          <img src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1100&q=85" alt="Calm office workspace" />
          <figcaption>Good<br />People<br />Great<br />Technology<i /></figcaption>
        </figure>
      </div>
    </section>
  );
}

function ApplicationModal({ role, onClose }) {
  const initialForm = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    resumeUrl: "",
    resumeName: "",
    message: "",
  };
  const [form, setForm] = React.useState({
    ...initialForm,
  });
  const [errors, setErrors] = React.useState({});
  const [status, setStatus] = React.useState("");
  const [statusKind, setStatusKind] = React.useState("error");
  const [isDragging, setIsDragging] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  function validateApplication(formValues) {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^\d{10}$/;

    if (!formValues.firstName.trim()) {
      nextErrors.firstName = "First name is required.";
    }

    if (!formValues.lastName.trim()) {
      nextErrors.lastName = "Last name is required.";
    }

    if (!formValues.email.trim()) {
      nextErrors.email = "Email address is required.";
    } else if (!emailPattern.test(formValues.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formValues.phone.trim()) {
      nextErrors.phone = "Phone number is required.";
    } else if (!phonePattern.test(formValues.phone.trim())) {
      nextErrors.phone = "Enter exactly 10 digits.";
    }

    if (!formValues.resumeUrl.trim()) {
      nextErrors.resume = "Resume upload is required.";
    }

    if (!formValues.message.trim()) {
      nextErrors.message = "Cover message is required.";
    } else if (formValues.message.trim().length < 10) {
      nextErrors.message = "Cover message must be at least 10 characters.";
    }

    return nextErrors;
  }

  function updateForm(event) {
    const { name, value } = event.target;
    const nextValue = name === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value;
    const nextForm = { ...form, [name]: nextValue };

    setForm(nextForm);
    setStatus("");

    if (errors[name]) {
      setErrors(validateApplication(nextForm));
    }
  }

  function readResumeFile(file) {
    if (!file) return;
    setStatusKind("error");

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      setStatus("Upload a PDF, DOC, or DOCX resume.");
      setErrors((current) => ({ ...current, resume: "Upload a PDF, DOC, or DOCX resume." }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      setForm((current) => ({
        ...current,
        resumeUrl: result.includes(",") ? result.split(",")[1] : result,
        resumeName: file.name,
      }));
      setErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors.resume;
        return nextErrors;
      });
      setStatus("");
    };
    reader.readAsDataURL(file);
  }

  function handleResumeUpload(event) {
    readResumeFile(event.target.files[0]);
    event.target.value = "";
  }

  function handleResumeDrop(event) {
    event.preventDefault();
    setIsDragging(false);
    readResumeFile(event.dataTransfer.files[0]);
  }

  async function submitApplication(event) {
    event.preventDefault();
    setStatusKind("error");

    const nextErrors = validateApplication(form);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showAppState("form-validation", { errors: nextErrors });
      setStatus("");
      return;
    }

    const apiUrl = getApiUrl();
    if (!apiUrl) {
      setStatus("API URL is not configured.");
      return;
    }

    setIsSubmitting(true);
    setStatus("");

    try {
      const response = await apiFetch(`${apiUrl}/careers/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          role,
          resumeUrl: form.resumeUrl.trim() || undefined,
          resumeName: form.resumeName.trim() || undefined,
          message: form.message.trim() || `Application for ${role}`,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setStatus(data.message || "Application could not be submitted.");
        return;
      }

      showAppState("success", { message: data.message || "Application submitted successfully." });
      setStatusKind("success");
      setStatus(data.message || "Application submitted successfully");
      setForm({ ...initialForm });
      setErrors({});
    } catch (error) {
      console.error("CAREER APPLY ERROR:", error);
      setStatus("Cannot connect to the backend server.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function fieldError(name) {
    return errors[name] ? <span className="field-error">{errors[name]}</span> : null;
  }

  return (
    <div className="application-backdrop job-details-backdrop" role="presentation" onClick={onClose}>
      <article className="application-modal" role="dialog" aria-modal="true" aria-label="Career application" onClick={(event) => event.stopPropagation()}>
        <button className="application-close" type="button" aria-label="Close" title="Close" onClick={onClose} />
        <div className="popup-content">
        <p className="kicker">Apply Now <span /></p>
        {role.trim().toLowerCase() !== "apply now" && <h2>{role}</h2>}
        <form onSubmit={submitApplication} noValidate>
          <div className="application-grid">
            <label>
              <span className="label-text">First Name <span className="required-star">*</span></span>
              <input className={errors.firstName ? "has-error" : ""} name="firstName" value={form.firstName} onChange={updateForm} placeholder="Enter first name" aria-invalid={Boolean(errors.firstName)} />
              {fieldError("firstName")}
            </label>
            <label>
              <span className="label-text">Last Name <span className="required-star">*</span></span>
              <input className={errors.lastName ? "has-error" : ""} name="lastName" value={form.lastName} onChange={updateForm} placeholder="Enter last name" aria-invalid={Boolean(errors.lastName)} />
              {fieldError("lastName")}
            </label>
            <label>
              <span className="label-text">Email Address <span className="required-star">*</span></span>
              <input className={errors.email ? "has-error" : ""} name="email" type="email" value={form.email} onChange={updateForm} placeholder="Enter your email" aria-invalid={Boolean(errors.email)} />
              {fieldError("email")}
            </label>
            <label>
              <span className="label-text">Phone Number <span className="required-star">*</span></span>
              <input className={errors.phone ? "has-error" : ""} name="phone" type="tel" value={form.phone} onChange={updateForm} placeholder="10 digit number" inputMode="numeric" maxLength={10} pattern="[0-9]{10}" aria-invalid={Boolean(errors.phone)} />
              {fieldError("phone")}
            </label>
          </div>
          <div className="resume-field">
          <label className="label-text" htmlFor="career-resume"> Upload Resume<span className="required-star">*</span></label>
          <label
            htmlFor="career-resume"
            className={`resume-dropzone ${isDragging ? "is-dragging" : ""} ${errors.resume ? "has-error" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleResumeDrop}
          >
            <input id="career-resume" type="file" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} aria-invalid={Boolean(errors.resume)} aria-describedby={errors.resume ? "career-resume-error" : undefined} />
            <span className="resume-upload-prompt">
              <FaCloudArrowUp className="resume-upload-icon" aria-hidden="true" />
              {form.resumeName || "Drop resume here or click to upload"}
            </span>
          </label>
          <div id="career-resume-error">{fieldError("resume")}</div>
          </div>
          <label>
           <span className="label-text">Cover Message <span className="required-star">*</span></span>
            <textarea className={errors.message ? "has-error" : ""} name="message" value={form.message} onChange={updateForm} placeholder="Tell us about your experience..." aria-invalid={Boolean(errors.message)} />
            {fieldError("message")}
          </label>
          {status && <p className={`application-status form-feedback form-${statusKind}`} role={statusKind === "error" ? "alert" : "status"}>{status}</p>}
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Application"} <FaArrowRight aria-hidden="true" />
          </button>
        </form>
        </div>
      </article>
    </div>
  );
}

function Positions({ jobs, onApply }) {
  const [active, setActive] = React.useState("All");
  const [selectedJob, setSelectedJob] = React.useState(null);
  const openJobs = jobs.filter(isOpenJob);
  const filters = ["All", ...new Set(openJobs.map((job) => job.team))];
  const visibleJobs = active === "All" ? openJobs : openJobs.filter((job) => job.team === active);
  const openJobDetails = (job) => setSelectedJob(job);
  const selectFilter = (filter) => setActive(filter);

  return (
    <section className="positions" id="positions">
      <div className="section-row">
        <p className="kicker">Open Positions <span /></p>
        <div className="filters">
          {filters.map((filter) => (
            <button className={active === filter ? "is-active" : ""} type="button" aria-pressed={active === filter} onClick={() => selectFilter(filter)} key={filter}>
              {filter}
            </button>
          ))}
        </div>
      </div>
      {openJobs.length === 0 ? <EmptyState /> : visibleJobs.length === 0 ? <NoSearchResults onReset={() => setActive("All")} /> : null}
      <div className="job-list">
        {visibleJobs.map((job) => (
          <article
            className="job-card"
            key={job.id || job.title}
            role="button"
            tabIndex={0}
            aria-label={`View details for ${job.title}`}
            onClick={() => openJobDetails(job)}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openJobDetails(job);
              }
            }}
          >
            <div className="job-summary">
              <h2>{job.title}</h2>
              <p><span aria-hidden="true">-</span> {job.location} <i /> <span aria-hidden="true">-</span> Full Time</p>
            </div>
            <button
              className="job-tag"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                openJobDetails(job);
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openJobDetails(job);
                }
              }}
            >
              {job.team}
            </button>
            <button className="job-apply" type="button" onClick={(event) => {
              event.stopPropagation();
              onApply(job.title);
            }}>
              Apply Now <FaArrowRight aria-hidden="true" />
            </button>
          </article>
        ))}
      </div>
      {selectedJob && (
        <JobDetailsModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onApply={() => {
            setSelectedJob(null);
            onApply(selectedJob.title);
          }}
        />
      )}
    </section>
  );
}

function normalizeDescriptionLines(value) {
  return String(value || "")
    .replace(/&#x20;/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => line.replace(/\\\s*$/, "").trim())
    .filter(Boolean);
}

function renderInlineText(line) {
  return line.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>;
    }

    return part;
  });
}

function sanitizeJobHtml(value) {
  if (!value || typeof window === "undefined") return "";

  const allowedTags = new Set(["P", "BR", "STRONG", "B", "EM", "I", "UL", "OL", "LI", "H3", "H4"]);
  const doc = new DOMParser().parseFromString(String(value), "text/html");

  doc.body.querySelectorAll("*").forEach((node) => {
    if (!allowedTags.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }

    Array.from(node.attributes).forEach((attribute) => {
      node.removeAttribute(attribute.name);
    });
  });

  return doc.body.innerHTML;
}

function isHtmlDescription(value) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
}

function JobDetailsModal({ job, onClose, onApply }) {
  const jobDescription = getJobDescription(job);
  const htmlDescription = isHtmlDescription(jobDescription)
    ? sanitizeJobHtml(jobDescription)
    : "";
  const descriptionLines = htmlDescription ? [] : normalizeDescriptionLines(jobDescription);
  const lines = descriptionLines.length
    ? descriptionLines
    : [
        "No job description has been added from admin for this role yet.",
        "Please update this role from the Career/Hiring panel to show the complete JD here.",
      ];

  return (
    <div className="application-backdrop job-details-backdrop" role="presentation" onClick={onClose}>
      <article className="job-details-modal" role="dialog" aria-modal="true" aria-label={`${job.title} job description`} onClick={(event) => event.stopPropagation()}>
        <button className="application-close" type="button" aria-label="Close" title="Close" onClick={onClose} />
        <div className="popup-content">
        <div className="job-details-header">
          <p className="kicker">Job Description <span /></p>
        </div>
        {htmlDescription ? (
          <div className="job-details-copy" dangerouslySetInnerHTML={{ __html: htmlDescription }} />
        ) : (
          <div className="job-details-copy">
            {lines.map((line, index) => {
              const heading = line.replace(/^#{1,6}\s+/, "");
              const isHeading = heading !== line;
              const isBullet = /^[-*]\s+/.test(line);
              const content = isBullet ? line.replace(/^[-*]\s+/, "") : heading;

              if (isHeading) {
                return <h3 key={`${line}-${index}`}>{renderInlineText(content)}</h3>;
              }

              return (
                <p className={isBullet ? "job-detail-bullet" : ""} key={`${line}-${index}`}>
                  {renderInlineText(content)}
                </p>
              );
            })}
          </div>
        )}
        <button className="job-details-apply" type="button" onClick={onApply}>
          Apply Now <FaArrowRight aria-hidden="true" />
        </button>
        </div>
      </article>
    </div>
  );
}

function ResumeCta({ onApply }) {
  return (
    <section className="resume-cta">
      <span className="mail-icon" aria-hidden="true">
        <FaArrowRight />
      </span>
      <div>
        <p className="kicker">Let's Connect</p>
        <h2>Don't see a role that fits?</h2>
        <p>Share your resume—we’ll contact you when a suitable role opens.</p>
        <p>We’re always keen to hear from talented people who want to build with us.</p>
      </div>
      <a href="#resume" onClick={(event) => { event.preventDefault(); onApply("Apply now"); }} className="resume-cta-button">
        Share Your Resume <FaArrowRight aria-hidden="true" />
      </a>
    </section>
  );
}

function App() {
  const [jobs, setJobs] = React.useState([]);
  const [applicationRole, setApplicationRole] = React.useState("");

  React.useEffect(() => {
    const apiUrl = getApiUrl();
    if (!apiUrl) return;

    apiFetch(`${apiUrl}/careers/jobs`)
      .then((response) => response.ok ? response.json() : Promise.reject(response))
      .then((data) => {
        if (Array.isArray(data)) {
          setJobs(data.map(normalizeJob));
        }
      })
      .catch((error) => {
        console.error("CAREER JOBS ERROR:", error);
      });
  }, []);

  return (
    <>
      <Header />
      <main className="careers-page">
        <Hero />
        <Positions jobs={jobs} onApply={setApplicationRole} />
        <ResumeCta onApply={setApplicationRole} />
      </main>
      <Footer />
      {applicationRole && (
        <ApplicationModal role={applicationRole} onClose={() => setApplicationRole("")} />
      )}
    </>
  );
}

export default App;
